import { defineStore } from 'pinia'
import apiClient from '../api/client'
import { SUPPORTED_LOCALES, loadLocaleMessages, type SupportedLocale } from '../locales'
import { i18n } from '../i18n'
import { useTenantTheme, type TenantTheme } from '../composables/useTenantTheme'
import { useTerminology } from '../composables/useTerminology'
import type { TerminologyPresetName } from '../terminology/presets'

interface User {
  id: number
  email: string
  name: string
  role: string
  tenant_id: number
  preferred_locale?: string | null
}

interface Tenant {
  id: number
  name: string
  slug: string
  theme?: TenantTheme | null
}

interface AuthState {
  token: string | null
  user: User | null
  tenant: Tenant | null
  isDemo: boolean
}

/**
 * Si l'user a une preferred_locale serveur, on l'applique à i18n (override
 * du localStorage). Best-effort : ne casse rien si pas supportée.
 */
async function applyServerLocale(locale: string | null | undefined): Promise<void> {
  if (!locale) return
  if (!(SUPPORTED_LOCALES as readonly string[]).includes(locale)) return
  const supportedLocale = locale as SupportedLocale

  if (!i18n.global.availableLocales.includes(supportedLocale)) {
    try {
      const messages = await loadLocaleMessages(supportedLocale)
      i18n.global.setLocaleMessage(supportedLocale, messages as any)
    } catch (e) {
      console.warn(`[auth] Failed to load server locale ${supportedLocale}`, e)
      return
    }
  }

  i18n.global.locale.value = supportedLocale
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('lang', supportedLocale)
  }
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem('user_locale', supportedLocale)
  }
}

/**
 * Applique le theme tenant complet (preset DS + CSS overrides + brand)
 * + la terminologie (preset + overrides).
 */
function applyTenantBranding(theme: TenantTheme | null | undefined): void {
  if (!theme) return

  const { applyTenantTheme } = useTenantTheme()
  const { applyTerminology } = useTerminology()

  applyTenantTheme(theme)
  applyTerminology(
    (theme.terminology_preset as TerminologyPresetName) || 'sds_default',
    theme.terminology_overrides,
  )
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    token: localStorage.getItem('sf_v2_token'),
    user: null,
    tenant: null,
    isDemo: localStorage.getItem('sf_v2_demo') === '1',
  }),

  getters: {
    isAuthenticated: (state) => !!state.token,
    tenantName: (state) => state.tenant?.theme?.brand_name || state.tenant?.name || '',
  },

  actions: {
    async login(email: string, password: string) {
      const res = await apiClient.post('/auth.login_v2', { email, password })
      this.token = res.data.token
      this.user = res.data.user
      this.tenant = res.data.tenant
      this.isDemo = false
      localStorage.setItem('sf_v2_token', this.token!)
      localStorage.removeItem('sf_v2_demo')
      await applyServerLocale(this.user?.preferred_locale)
      applyTenantBranding(this.tenant?.theme)
    },

    /**
     * Auto-login sur compte demo (whitelist par role). Pas de mot de passe.
     * Marque la session en mode demo (bandeau jaune affichee + sf_v2_demo
     * dans localStorage).
     */
    async demoLogin(role: 'admin' | 'direction' | 'conducteur' | 'chef' | 'compagnon') {
      const res = await apiClient.post('/auth.demo_login', { role })
      this.token = res.data.token
      this.user = res.data.user
      this.tenant = res.data.tenant
      this.isDemo = true
      localStorage.setItem('sf_v2_token', this.token!)
      localStorage.setItem('sf_v2_demo', '1')
      await applyServerLocale(this.user?.preferred_locale)
      applyTenantBranding(this.tenant?.theme)
    },

    async fetchMe() {
      if (!this.token) return
      const res = await apiClient.get('/auth.me')
      this.user = res.data.user
      this.tenant = res.data.tenant
      await applyServerLocale(this.user?.preferred_locale)
      applyTenantBranding(this.tenant?.theme)
    },

    /**
     * Met à jour le branding du tenant courant et réapplique au runtime.
     * Admin only — appelé depuis /admin/branding.
     */
    async updateBranding(patch: Partial<TenantTheme>) {
      const res = await apiClient.post('/tenant.update_branding', patch)
      if (res.data.ok) {
        // Refresh le theme tenant depuis le backend
        const themeRes = await apiClient.get('/tenant.theme')
        if (this.tenant) {
          this.tenant.theme = themeRes.data.theme
        }
        applyTenantBranding(themeRes.data.theme)
      }
    },

    logout() {
      this.token = null
      this.user = null
      this.tenant = null
      this.isDemo = false
      localStorage.removeItem('sf_v2_token')
      localStorage.removeItem('sf_v2_demo')

      // Reset theme + terminology aux valeurs par défaut
      const { clearTenantTheme } = useTenantTheme()
      const { clearTerminology } = useTerminology()
      clearTenantTheme()
      clearTerminology()
    },
  },
})
